<template>
  <TransitionRoot appear :show="modalStore.isOpen" as="template">
    <Dialog as="div" @close="modalStore.close()" class="relative z-50">
      <TransitionChild as="template" enter="duration-200 ease-out" enter-from="opacity-0" enter-to="opacity-100"
        leave="duration-150 ease-in" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/50 backdrop-blur-[1.5px]" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto overscroll-contain">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild as="template" enter="duration-200 ease-out"
            enter-from="opacity-0 translate-y-1 scale-[0.985]" enter-to="opacity-100 translate-y-0 scale-100"
            leave="duration-150 ease-in" leave-from="opacity-100 translate-y-0 scale-100"
            leave-to="opacity-0 translate-y-1 scale-[0.985]">
            <DialogPanel class="modal-panel mx-2">
              <header class="modal-header">
                <div class="flex items-start gap-3">
                  <ResourceIconFrame v-if="modalStore.headerIcon" size="lg">
                    <component :is="modalStore.headerIcon" class="size-5" />
                  </ResourceIconFrame>
                  <div class="min-w-0">
                    <DialogTitle as="h2" class="text-base font-semibold text-white">
                      {{ modalStore.title }}
                    </DialogTitle>
                    <DialogDescription v-if="modalStore.description" class="mt-1 text-sm text-gray-400">
                      {{ modalStore.description }}
                    </DialogDescription>
                  </div>
                </div>
              </header>

              <div class="modal-content">
                <component :is="modalStore.view" v-bind="modalStore.props" v-on="modalStore.listeners"
                  @close="modalStore.close()" ref="dynamicComponent" />
              </div>

              <p v-if="submitError" class="px-5 pb-2 text-xs text-rose-300">
                {{ submitError }}
              </p>

              <div v-if="modalStore.buttons.length" class="modal-footer">
                <AppButton v-for="(button, index) in modalStore.buttons" :key="index" type="button"
                  :variant="button.variant ?? 'secondary'" :size="button.size ?? 'md'" :disabled="button.disabled ?? false"
                  @click="() => handleButtonClick(button)">
                  {{ button.text }}
                </AppButton>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from '@headlessui/vue'
import { useModalStore } from '@/stores/modal'
import { getApiErrorMessage } from '@/services/api-error'
import ResourceIconFrame from '@/components/resources/ResourceIconFrame.vue'

export default {
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogDescription,
    ResourceIconFrame,
  },
  data() {
    return {
      submitError: null,
    }
  },
  computed: {
    modalStore() {
      return useModalStore()
    },
  },
  watch: {
    'modalStore.isOpen'(isOpen) {
      if (isOpen) {
        this.submitError = null
      }
    },
  },
  methods: {
    async handleButtonClick(button) {
      this.submitError = null

      if (button.action === 'submit' && this.$refs.dynamicComponent) {
        try {
          await this.$refs.dynamicComponent.submit()
        } catch (error) {
          this.submitError = getApiErrorMessage(error, this.$t('app.errors.submitFailed'))
        }

        return
      }

      if (button.action === 'close') {
        this.modalStore.close()
      } else if (button.onClick) {
        button.onClick()
      }
    }
  }
}
</script>
